import { useMemo } from 'react';
import { Tag } from '@humanforce/mango-ui-react';
import type { PayRunApprovalDto, PayRunDetailDto } from './payRunsApiTypes';
import type { PayRunExceptionsPage } from './payRunPopulationApiTypes';
import type { PayRunBankingInput } from './payRunBankingModel';
import {
  buildPayRunTimeline,
  type PayRunTimelineActionVerb,
} from './payRunTimelineModel';
import { LifecycleStepper } from './LifecycleStepper';
import type { LifecycleStep } from './lifecycleStepperTypes';
import { PopoverGroupProvider } from './usePopoverGroup';
import './PayRunTimeline.css';

interface PayRunTimelineProps {
  payRun: PayRunDetailDto;
  approval: PayRunApprovalDto | null;
  exceptions: PayRunExceptionsPage | null;
  busy?: boolean;
  /** Optional Banking (BSB) evidence for the Review stage. Omitted callers keep the pre-PRR-315 behaviour. */
  banking?: PayRunBankingInput;
  onAction: (verb: PayRunTimelineActionVerb) => void;
  showOperatingState?: boolean;
}

export const PayRunTimeline = ({
  payRun,
  approval,
  exceptions,
  busy = false,
  banking,
  onAction,
  showOperatingState = true,
}: PayRunTimelineProps) => {
  const timeline = useMemo(
    () => buildPayRunTimeline({ payRun, approval, exceptions, banking }),
    [payRun, approval, exceptions, banking],
  );

  // Adapter: the stepper is domain-free, so the pay run's own vocabulary is translated here.
  const steps: LifecycleStep[] = useMemo(() => timeline.steps.map((step) => ({
      id: step.id,
      title: step.title,
      detail: step.detail,
      // The stage the run is on is the busy one: it carries the spinner, and the spinner moves on
      // when the stage completes. Nothing animates on the thread itself unless the figures move.
      state: step.state === 'in_progress' ? 'busy' : step.state,
      menuLabel: step.menuLabel,
      rows: step.subSteps.map((subStep) => ({
        id: subStep.id,
        title: subStep.label,
        detail: subStep.detail,
        state: subStep.state,
        person: subStep.person,
      })),
      footer: step.footer ?? null,
      action: step.action
        ? { label: step.action.label, onSelect: () => onAction(step.action!.verb) }
        : null,
      connectorPercent: step.progressPercent ?? undefined,
    })), [timeline, onAction]);

  return (
    <section className="mgo-stack mgo-gap-s" aria-labelledby="pay-run-timeline-heading">
      <div className="mgo-cluster mgo-gap-s" data-testid="pay-run-timeline-operating-state">
        <span id="pay-run-timeline-heading" className="sr-only">Pay run progress</span>
        {showOperatingState && (
          <Tag
            label={timeline.operatingStateLabel}
            tone={timeline.operatingStateTone === 'green' ? 'positive' : 'neutral'}
          />
        )}
      </div>
      <div className="pay-run-timeline" data-testid="pay-run-timeline">
        <PopoverGroupProvider>
          <LifecycleStepper
            label="Pay run progress"
            steps={steps}
            currentStepId={timeline.currentStepId}
            busy={busy}
            motion="progress-line"
            stepTestIdPrefix="pay-run-timeline-step"
            menuTestIdPrefix="pay-run-timeline-menu"
          />
        </PopoverGroupProvider>
      </div>
    </section>
  );
};
